import { pageTitle } from '@palaccount/config';
import {
  OVERRIDE_NEGATIVE_STOCK,
  type DeliveryLineInput,
  type DeliveryNoteResponse,
} from '@palaccount/contracts';
import { Plus, Trash2, Truck } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';

import { Amount, Ltr, Qty } from '@/components/amount';
import { DeleteConfirmBox } from '@/components/confirm-box';
import { useUnsavedChanges } from '@/components/document-header-controls';
import { DocumentToolbar } from '@/components/document-toolbar';
import { useLineImport } from '@/features/documents/line-tools';
import { DescriptionLangToggle, useDescriptionLanguage } from '@/features/documents/description-language';
import { AttachmentsPanel } from '@/features/attachments/attachments-panel';
import { DocumentActions } from '@/features/documents/document-actions';
import { deliveryNoteDocument } from '@/features/documents/record-documents';
import { Gated } from '@/components/permission-gate';
import { ReasonBox } from '@/components/reason-box';
import { DocStatusChip } from '@/components/status-chip';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { Typeahead } from '@/components/ui/typeahead';
import { useItemsHeld } from '@/features/inventory/api';
import { ItemCell } from '@/features/inventory/item-cell';
import { useItemPicker } from '@/features/inventory/item-picker';
import { trimZeros } from '@/features/inventory/conversion';
import { toMilliQty } from '@/features/inventory/stock-format';
import { useWarehouses } from '@/features/inventory/stock-api';
import { useOrgSetting } from '@/features/admin/api';
import { usePartnersHeld } from '@/features/partners/api';
import { usePartnerPicker } from '@/features/partners/partner-picker';
import { PartnerTypeahead } from '@/features/partners/partner-typeahead';
import { PartnerBalanceChip } from '@/features/partners/partner-balance-chip';
import { denyPermission, useCan } from '@/lib/permissions';
import { ApiError, localizeServerCause } from '@/lib/server-messages';
import { cn } from '@/lib/utils';
import { useBaseCurrency, useDocumentDate } from '@/lib/session';

import {
  useCancelDelivery,
  useCreateDelivery,
  useDeleteDeliveries,
  useDeleteDelivery,
  useDelivery,
  usePostDelivery,
  useRecordDeliveryProof,
  useUpdateDelivery,
} from './deliveries-api';
import { fromLocalDateTimeInput, isShortDelivery, toLocalDateTimeInput } from './delivery-rules';
import { LineStockHint, OverStockStrip, shortBy } from './line-stock-warning';
import { useOrder, useOrderPicker } from './orders-api';
import {
  SalesHeaderFields,
  salesHeaderCreateBody,
  salesHeaderFromDoc,
  salesHeaderPatchBody,
  useSalesHeaderState,
} from './sales-header-fields';
import {
  captureBlocks,
  textFromStored,
  trackingPayload,
  type CaptureText,
  type TrackedItem,
} from './tracking-capture';
import { TrackingRows } from './tracking-rows';

interface EditLine {
  /** The row's own identity — an order line's number, or a fresh id for an extra line. */
  key: string;
  /** Null on an extra line (wave 5B): there is no order line behind it. */
  soLineNo: number | null;
  /**
   * Wave 5B — a line NOT on the order (a sample, a replacement). Names its
   * item outright, carries a reason, shows no ordered / remaining figure and
   * never wears the Backorder chip. Never invoiced automatically.
   */
  isExtra: boolean;
  extraReason: string;
  /** '' on an extra row until an item is picked. */
  itemId: string;
  itemCode: string;
  description: string;
  ordered: string;
  /**
   * What was LEFT for this note to send — the server's ordered − Σ earlier
   * posted notes (BR-SAL-03-02's ledger). The Backorder chip compares the
   * typed quantity against THIS, never against `ordered`.
   */
  remaining: string;
  qty: string;
  /**
   * The warehouse this line actually ships from: the order line's own, else
   * the note's header one — resolved by the server (`soLine.warehouseId ??
   * header`) and carried on the line. The stock warning reads THIS shelf; it
   * used to read the header's and warned about the wrong room.
   */
  warehouseId: string;
  /**
   * `N-4` — the unit both figures on this row are in. It comes off the LINE
   * rather than being looked up: a delivery note is raised against an order
   * whose lines are fixed, so the row's unit is the one the server resolved
   * for it, and there is no picker on this grid to make it stale.
   */
  unitSymbol: string;
  unitPrecision: number;
  /**
   * OQ-05-17's blocks, keyed the way `tracking-capture` keys them. The grid
   * had ONE cell here, which could only ever hold a plain line's own
   * identifiers — a kit line whose parts are tracked has no single answer,
   * because each part follows its OWN rules (R7, BR-MD-03-17).
   */
  tracking: CaptureText;
  note: string;
}

let extraKeySeq = 0;
const nextExtraKey = (): string => `extra-${(extraKeySeq += 1)}`;

function fromDelivery(delivery: DeliveryNoteResponse): EditLine[] {
  return delivery.lines.map((line) => ({
    key: line.isExtra ? `extra-${line.id}` : `so-${line.soLineNo ?? line.id}`,
    soLineNo: line.soLineNo,
    isExtra: line.isExtra,
    extraReason: line.extraReason ?? '',
    itemId: line.itemId,
    itemCode: line.itemCode,
    description: line.description,
    ordered: trimZeros(line.qtyOrdered),
    remaining: trimZeros(line.remaining),
    qty: trimZeros(line.qty),
    warehouseId: line.warehouseId,
    unitSymbol: line.unitSymbol,
    unitPrecision: line.unitPrecision,
    tracking: textFromStored(line),
    note: line.note ?? '',
  }));
}

/** Wave 5B — the logistics strip (SAL-03 §5a), draft-side like every header field. */
interface LogisticsState {
  driver: string;
  vehicleNo: string;
  vehicleType: string;
  /** The `datetime-local` box's value — local wall-clock; the wire gets the instant. */
  departedAt: string;
  deliveryAddress: string;
}

const emptyLogistics = (): LogisticsState => ({
  driver: '',
  vehicleNo: '',
  vehicleType: '',
  departedAt: '',
  deliveryAddress: '',
});

function logisticsFromDoc(delivery: DeliveryNoteResponse): LogisticsState {
  return {
    driver: delivery.driver ?? '',
    vehicleNo: delivery.vehicleNo ?? '',
    vehicleType: delivery.vehicleType ?? '',
    departedAt: toLocalDateTimeInput(delivery.departedAt),
    deliveryAddress: delivery.deliveryAddress ?? '',
  };
}

/** The CREATE body: empties omitted (the server prefills the address from the customer). */
function logisticsCreateBody(state: LogisticsState) {
  const departedAt = fromLocalDateTimeInput(state.departedAt);
  return {
    ...(state.driver.trim() ? { driver: state.driver.trim() } : {}),
    ...(state.vehicleNo.trim() ? { vehicleNo: state.vehicleNo.trim() } : {}),
    ...(state.vehicleType.trim() ? { vehicleType: state.vehicleType.trim() } : {}),
    ...(departedAt !== undefined ? { departedAt } : {}),
    ...(state.deliveryAddress.trim() ? { deliveryAddress: state.deliveryAddress.trim() } : {}),
  };
}

/** The PATCH body: null clears, so a field emptied on screen empties on the row. */
function logisticsPatchBody(state: LogisticsState) {
  return {
    driver: state.driver.trim() || null,
    vehicleNo: state.vehicleNo.trim() || null,
    vehicleType: state.vehicleType.trim() || null,
    departedAt: fromLocalDateTimeInput(state.departedAt) ?? null,
    deliveryAddress: state.deliveryAddress.trim() || null,
  };
}

/** Wave 5B — the proof-of-delivery block (SAL-03 §5d), written on a confirmed note. */
interface ProofState {
  receivedBy: string;
  receivedAt: string;
  receiverNote: string;
}

function proofFromDoc(delivery: DeliveryNoteResponse): ProofState {
  return {
    receivedBy: delivery.receivedBy ?? '',
    receivedAt: toLocalDateTimeInput(delivery.receivedAt),
    receiverNote: delivery.receiverNote ?? '',
  };
}

function CellInput({ mono, className, ...props }: React.ComponentProps<'input'> & { mono?: boolean }) {
  return (
    <input
      className={cn(
        'h-9 w-full min-w-0 rounded-md border border-transparent bg-transparent px-2 text-sm outline-none transition-colors',
        'hover:border-input focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/50',
        'disabled:cursor-not-allowed disabled:opacity-70',
        mono && 'text-end font-mono tabular-nums',
        className,
      )}
      {...props}
    />
  );
}

/**
 * SAL-03's editor — what actually left the warehouse, no money anywhere.
 * Creating a note IS the load: the server fills every item-linked order
 * line at its REMAINING quantity (two notes never claim the same units,
 * BR-SAL-03-02); the clerk lowers what did not fit on the vehicle — the
 * short lines turn amber with the Backorder chip (BR-SAL-03-05) — and
 * Confirm delivery is the once-only posting step (BR-SAL-03-03/-07).
 */
export function DeliveryEditorPage() {
  // GEN-107: what these figures are IN, from the session rather than a
  // literal. Every one of them is a ledger or stock valuation, which the
  // company holds in its base currency; nothing here converts, so GEN-116
  // keeps the disclosure off the screen.
  const baseCurrency = useBaseCurrency();
  const { t, i18n } = useTranslation();
  // The reader's language is the PDF's; `print.language` may override it in
  // the template (INV-H6), for the worker and the browser alike.
  const printLocale = i18n.language.startsWith('ar') ? ('ar' as const) : ('en' as const);
  const navigate = useNavigate();
  const { deliveryId } = useParams<{ deliveryId: string }>();
  const isNew = !deliveryId;
  // D-09: arriving from the order tracker's Delivery step (?orderId=) — the
  // order is pre-picked, the customer follows it; the picker's held read
  // fills the option in.
  const [searchParams] = useSearchParams();

  const { data: delivery, isPending: loading } = useDelivery(deliveryId);
  const { data: warehouses } = useWarehouses();
  const createDelivery = useCreateDelivery();
  const updateDelivery = useUpdateDelivery(deliveryId ?? '');
  const deleteDelivery = useDeleteDelivery(deliveryId ?? '');
  const deleteDeliveries = useDeleteDeliveries();
  const postDelivery = usePostDelivery(deliveryId ?? '');
  const cancelDelivery = useCancelDelivery(deliveryId ?? '');
  const recordProof = useRecordDeliveryProof(deliveryId ?? '');
  // The order behind the note — read for its STATUS after a confirm: a
  // 'fulfilled' order has leftover drafts to offer deleting, a
  // 'partially_delivered' one has a follow-up note to offer raising. The
  // post invalidates ORDERS_KEY, so this refetches on its own.
  const canEdit = useCan(isNew ? 'sales.create' : 'sales.edit');

  const [salesOrderId, setSalesOrderId] = useState(
    isNew ? (searchParams.get('orderId') ?? '') : '',
  );
  /**
   * Wave 5B — DIRECT DELIVERY: a note raised to a customer with no order
   * behind it. Offered only when the company switched it on
   * (`sales.direct_delivery_enabled`, off by default) — the server refuses
   * with `directDeliveryDisabled` otherwise, so hiding the choice is the
   * screen agreeing with the rule rather than enforcing it. The create act
   * itself stays `sales.create`.
   */
  const directEnabled = useOrgSetting<boolean>('sales.direct_delivery_enabled') === true;
  const [mode, setMode] = useState<'order' | 'direct'>('order');
  const direct = isNew ? mode === 'direct' && directEnabled : false;
  const [directPartnerId, setDirectPartnerId] = useState('');
  const customerPicker = usePartnerPicker(direct ? directPartnerId || null : null, {
    type: 'customer',
  });
  // On a NEW note the picked order is read too — for its customer, whose
  // balance chip sits under the field before the note exists.
  const { data: order } = useOrder(
    isNew ? (direct ? undefined : salesOrderId || undefined) : (delivery?.salesOrderId ?? undefined),
  );
  /** A saved note with no order — wave 5B's direct shape. */
  const isDirectNote = !isNew && delivery !== undefined && delivery.salesOrderId === null;
  const [deliveryDate, setDeliveryDate] = useDocumentDate();
  const [warehouseId, setWarehouseId] = useState('');
  /** Wave 5B — INV-F2…F6 on the note (the shared sales header, minus warehouse and currency). */
  const [header, setHeader] = useSalesHeaderState(isNew);
  const [logistics, setLogistics] = useState<LogisticsState>(emptyLogistics);
  const [proof, setProof] = useState<ProofState>({ receivedBy: '', receivedAt: '', receiverNote: '' });
  const [lines, setLines] = useState<EditLine[]>([]);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  /**
   * The customer behind the note — the saved note's, else the picked order's
   * — held for the balance chip (MD-01 field 29 + the credit bar, the
   * server's own verdict). An editor that cannot list partners simply draws
   * no chip.
   */
  const partnerId = delivery?.partnerId ?? (direct ? directPartnerId : order?.partnerId) ?? '';
  const { data: heldPartners } = usePartnersHeld(useMemo(() => (partnerId ? [partnerId] : []), [partnerId]));
  const partner = heldPartners?.find((candidate) => candidate.id === partnerId);

  // GEN-50 — BR-SAL-03's deliverable orders, searched rather than listed.
  const orderPicker = useOrderPicker(salesOrderId || null, { deliverable: true });
  /*
   * The catalogue is where a line's tracking lives (MD-03 panel 7 and, for a
   * kit, panel 2's components) — the delivery note itself carries no such
   * column, and R7 says it must not: the component's record owns it. This is
   * a LOOKUP, not a picker: the lines come from the order, so it asks for
   * exactly the items they name and never for the catalogue.
   */
  const { data: items } = useItemsHeld(
    useMemo(() => lines.map((line) => line.itemId), [lines]),
  );
  /**
   * Wave 5B — the picker behind an EXTRA row: a search over the active
   * catalogue (GEN-50), holding whatever the extra rows already name. Order
   * rows never pick; their items are the order's.
   */
  const itemPicker = useItemPicker(
    useMemo(() => lines.filter((line) => line.isExtra).map((line) => line.itemId), [lines]),
    { status: 'active' },
  );
  const itemFor = (line: EditLine) =>
    (items ?? []).find((candidate) => candidate.id === line.itemId) ??
    itemPicker.byId.get(line.itemId);
  const [posted, setPosted] = useState(false);
  const [busy, setBusy] = useState(false);
  /** GEN-72's ask-why dialog for the posted-delivery cancel. */
  const [cancelAsk, setCancelAsk] = useState(false);
  /**
   * BR-INV-04-08's second question, asked on screen: confirming a note the
   * stock strip says is short takes a shelf below zero, so the confirming
   * person says why first — the same ReasonBox shape as the cancel, and
   * the reason rides the post's audit row.
   */
  const [negativeAsk, setNegativeAsk] = useState(false);
  const [deleting, setDeleting] = useState(false);
  /** "Delete leftover drafts" asks first (GEN-58's box) — it deletes several. */
  const [leftoverAsk, setLeftoverAsk] = useState(false);
  const loadedId = useRef<string | null>(null);
  /** S4 — the header rail's ‹‹ ›› wait while there is typed work (see `useUnsavedChanges`). */
  const dirty = useUnsavedChanges(
    [salesOrderId, deliveryDate, warehouseId, lines, notes],
    isNew ? 'new' : delivery && loadedId.current === delivery.id ? delivery.id : null,
  );

  const status = delivery?.status ?? 'draft';
  const isDraft = status === 'draft';
  const editable = isDraft && canEdit && !isNew;

  useEffect(() => {
    document.title = pageTitle(t('dn.editorTitle'));
  }, [t]);

  useEffect(() => {
    if (delivery && loadedId.current !== delivery.id) {
      loadedId.current = delivery.id;
      setDeliveryDate(delivery.deliveryDate);
      setWarehouseId(delivery.warehouseId);
      setHeader(salesHeaderFromDoc(delivery));
      setLogistics(logisticsFromDoc(delivery));
      setProof(proofFromDoc(delivery));
      setLines(fromDelivery(delivery));
      setNotes(delivery.notes ?? '');
    }
  }, [delivery]);

  const activeWarehouses = (warehouses ?? []).filter((warehouse) => warehouse.isActive);

  const setLine = (key: string, patch: Partial<EditLine>) => {
    setLines((current) => current.map((line) => (line.key === key ? { ...line, ...patch } : line)));
  };

  /** Wave 5B — a fresh extra row: no item yet, the header warehouse, nothing typed. */
  const addExtraLine = () => {
    setLines((current) => [
      ...current,
      {
        key: nextExtraKey(),
        soLineNo: null,
        isExtra: true,
        extraReason: '',
        itemId: '',
        itemCode: '',
        description: '',
        ordered: '0',
        remaining: '0',
        qty: '',
        warehouseId,
        unitSymbol: '',
        unitPrecision: 0,
        tracking: {},
        note: '',
      },
    ]);
  };

  const removeLine = (key: string) => {
    setLines((current) => current.filter((line) => line.key !== key));
  };

  /** Picking an item on an extra row adopts its code, name and stock unit. */
  const pickExtraItem = (key: string, itemId: string) => {
    const item = itemPicker.byId.get(itemId);
    setLine(key, {
      itemId,
      itemCode: item?.code ?? '',
      description: item ? descLang.describe(item) : '',
      unitSymbol: item?.stockUnitSymbol ?? '',
      unitPrecision: item?.stockUnitPrecision ?? 0,
      tracking: {},
    });
  };

  /**
   * What this line has to identify before the goods can leave. The item's own
   * record answers it — one block for a tracked item, one per tracked PART
   * when an auto-deducting kit expands (R7) — so the grid never has to guess
   * from what somebody typed whether a string is a serial or a lot.
   */
  const blocksFor = (line: EditLine) =>
    captureBlocks(itemFor(line) as TrackedItem | undefined, line.qty);

  /**
   * INV-L6 — the invoice grid's stock warning, on the delivery note too (see
   * `line-stock-warning.tsx`). Each line is read in the warehouse it SHIPS
   * FROM — the order line's own, else the note's header one, exactly as the
   * server resolves it (`soLine.warehouseId ?? header`) and carries on the
   * line — and against ON-HAND: the confirmed order already reserved these
   * goods, so "available" would count this very reservation and warn on
   * every full delivery. Warn, never block — the posting refusal is the
   * server's. A line read before the server answered (no warehouse yet)
   * falls back to the header's pick.
   */
  const lineShort = (line: EditLine): string | null =>
    editable
      ? shortBy(itemFor(line), line.qty, {
          warehouseId: line.warehouseId || warehouseId,
          measure: 'onHand',
        })
      : null;
  const anyShort = lines.some((line) => lineShort(line) !== null);
  /** The other drafts against this order, as the server reports them. */
  const pendingDraftIds = delivery?.pendingDraftIds ?? [];
  const pendingDraftCodes = delivery?.pendingDraftCodes ?? [];

  const setTracking = (lineKey: string, key: string, value: string) => {
    setLines((current) =>
      current.map((line) =>
        line.key === lineKey ? { ...line, tracking: { ...line.tracking, [key]: value } } : line,
      ),
    );
  };

  /**
   * The grid as the server takes it: an order row names its order line, an
   * extra row (wave 5B) names its item and reason. Every row sends the
   * warehouse it ships from — the per-line select (wave 5B) — so the server
   * stores what the screen showed, never a default the clerk did not see.
   */
  const requestLines = (): DeliveryLineInput[] =>
    lines.map((line) => {
      const common = {
        qty: line.qty.trim() === '' ? '0' : line.qty.trim(),
        ...(line.warehouseId ? { warehouseId: line.warehouseId } : {}),
        ...trackingPayload(blocksFor(line), line.tracking),
        ...(line.note.trim() !== '' ? { note: line.note.trim() } : {}),
      };
      if (line.isExtra) {
        return {
          isExtra: true as const,
          itemId: line.itemId,
          extraReason: line.extraReason.trim(),
          ...common,
        };
      }
      return { soLineNo: line.soLineNo ?? 0, ...common };
    });

  /** Wave 5B — an extra row needs its item and its reason before it can be sent. */
  const extraLinesIncomplete = (): string | null => {
    if (lines.some((line) => line.isExtra && line.itemId === '')) return t('dn.errExtraNeedsItem');
    if (lines.some((line) => line.isExtra && line.extraReason.trim() === '')) {
      return t('dn.errExtraNeedsReason');
    }
    return null;
  };

  const onCreate = async () => {
    setError(null);
    if (direct && !directPartnerId) {
      setError(t('dn.errNoCustomer'));
      return;
    }
    if (!direct && !salesOrderId) {
      // ERR-SAL-03-06's shape: type an order first.
      setError(t('dn.errNoOrder'));
      return;
    }
    if (!warehouseId) {
      setError(t('dn.errNoWarehouse'));
      return;
    }
    setBusy(true);
    try {
      // The note's own warehouse is the required origin; the shared body's
      // optional one is dropped in its favour.
      const { warehouseId: _headerWarehouse, ...headerBody } = salesHeaderCreateBody(header);
      const created = await createDelivery.mutateAsync({
        // Wave 5B: a direct note names its customer; an order-backed one its order.
        ...(direct ? { partnerId: directPartnerId } : { salesOrderId }),
        deliveryDate,
        warehouseId,
        ...headerBody,
        ...logisticsCreateBody(logistics),
        ...(notes.trim() ? { notes } : {}),
      });
      void navigate(`/sales/deliveries/${created.id}`, { replace: true });
    } catch (thrown) {
      if (thrown instanceof ApiError && thrown.status === 403) denyPermission('sales.create');
      // The line above already reads the cause to spot a 403; every strip on
      // this screen used to throw that same cause away one expression later.
      // `localizeServerCause` keeps it: a refusal that ARRIVED keeps the
      // server's sentence, localized, and a request that never came back gets
      // this screen's own sentence rather than the browser's `Failed to fetch`.
      // All five paths name `dn.errSaveFailed` — one screen, one sentence.
      setError(localizeServerCause(t, thrown, 'dn.errSaveFailed'));
    } finally {
      setBusy(false);
    }
  };

  /**
   * Wave 4C — lines from a small .xlsx/.csv on a delivery note. The order's
   * own lines are fixed (they come off the order), so what a file can add is
   * wave 5B's EXTRA lines only: each names an item (a code that matches none
   * is listed, never dropped quietly) and still needs its reason typed before
   * the note can be sent. Price and discount columns are ignored — a
   * delivery note carries no money.
   */
  /** Wave 4C — which name a picked/imported item writes into a NEW line description. */
  const descLang = useDescriptionLanguage();
  const lineImport = useLineImport({
    id: 'delivery',
    side: 'sales',
    describe: descLang.describe,
    // A delivery note carries no money: the copy has codes and quantities.
    copyLines: () =>
      lines
        .filter((line) => line.itemId !== '')
        .map((line) => ({
          code: line.itemCode,
          description: line.description,
          qty: line.qty,
          unitPrice: '',
          discountPct: '',
        })),
    requireItem: true,
    visible: editable,
    gate: { permission: 'sales.create', reasonKey: 'perm.cannot.createDeliveries' },
    disabled: busy,
    onLines: (imported) =>
      setLines((current) => [
        ...current,
        ...imported.map((line) => ({
          key: nextExtraKey(),
          soLineNo: null,
          isExtra: true,
          extraReason: '',
          itemId: line.itemId,
          itemCode: line.item?.code ?? line.code,
          description: line.description,
          ordered: '0',
          remaining: '0',
          qty: line.qty,
          warehouseId,
          unitSymbol: line.item?.stockUnitSymbol ?? '',
          unitPrecision: line.item?.stockUnitPrecision ?? 0,
          tracking: {},
          note: '',
        })),
      ]),
  });

  const onSave = async (): Promise<boolean> => {
    setError(null);
    const incomplete = extraLinesIncomplete();
    if (incomplete !== null) {
      setError(incomplete);
      return false;
    }
    setBusy(true);
    try {
      const { warehouseId: _headerWarehouse, ...headerPatch } = salesHeaderPatchBody(header);
      await updateDelivery.mutateAsync({
        deliveryDate,
        ...(warehouseId ? { warehouseId } : {}),
        ...headerPatch,
        ...logisticsPatchBody(logistics),
        lines: requestLines(),
        notes: notes.trim() === '' ? null : notes,
      });
      loadedId.current = null;
      return true;
    } catch (thrown) {
      if (thrown instanceof ApiError && thrown.status === 403) denyPermission('sales.edit');
      setError(localizeServerCause(t, thrown, 'dn.errSaveFailed'));
      return false;
    } finally {
      setBusy(false);
    }
  };

  /**
   * Confirm. When the stock strip reports a shortage the note is about to
   * take a shelf below zero, which needs `inventory.override_negative_stock`
   * AND a reason (BR-INV-04-08's second half) — so Confirm opens the
   * ReasonBox instead of posting, and `confirmPost` runs once it is
   * answered. The Confirm act's gate switches to the override grant in that
   * state (see the toolbar), so a role without it meets a greyed button with
   * the reason rather than a server refusal after the fact.
   */
  const onPost = async () => {
    if (anyShort) {
      setNegativeAsk(true);
      return;
    }
    await confirmPost();
  };

  const confirmPost = async (negativeStockReason?: string) => {
    setNegativeAsk(false);
    if (!(await onSave())) return;
    setBusy(true);
    try {
      await postDelivery.mutateAsync(
        negativeStockReason !== undefined ? { negativeStockReason } : {},
      );
      loadedId.current = null;
      setPosted(true);
    } catch (thrown) {
      if (thrown instanceof ApiError && thrown.status === 403) denyPermission('sales.approve');
      setError(localizeServerCause(t, thrown, 'dn.errSaveFailed'));
    } finally {
      setBusy(false);
    }
  };

  /**
   * The leftover drafts, once this note completed the order: each one
   * defaulted to the same remainder this note just sent and can never post
   * (BR-SAL-03-02's cap is cumulative over POSTED notes). Deleted through
   * the ordinary delete route, one by one, after GEN-58's box.
   */
  const onDeleteLeftovers = async () => {
    setLeftoverAsk(false);
    setBusy(true);
    setError(null);
    try {
      await deleteDeliveries.mutateAsync(pendingDraftIds);
      loadedId.current = null;
    } catch (thrown) {
      if (thrown instanceof ApiError && thrown.status === 403) denyPermission('sales.delete');
      setError(localizeServerCause(t, thrown, 'dn.errSaveFailed'));
    } finally {
      setBusy(false);
    }
  };

  /**
   * Wave 5B — the proof of delivery, saved through its own route: the one
   * write a confirmed note takes beside its notes. `receivedBy` is the
   * thing a proof cannot do without (BR-SAL-03-08's first clause); the
   * time, when the box is empty, is the server's now.
   */
  const onSaveProof = async () => {
    setError(null);
    if (proof.receivedBy.trim() === '') {
      setError(t('dn.errReceivedByRequired'));
      return;
    }
    setBusy(true);
    try {
      const receivedAt = fromLocalDateTimeInput(proof.receivedAt);
      await recordProof.mutateAsync({
        receivedBy: proof.receivedBy.trim(),
        ...(receivedAt !== undefined ? { receivedAt } : {}),
        ...(proof.receiverNote.trim() !== '' ? { receiverNote: proof.receiverNote.trim() } : {}),
      });
      loadedId.current = null;
    } catch (thrown) {
      if (thrown instanceof ApiError && thrown.status === 403) denyPermission('sales.edit');
      setError(localizeServerCause(t, thrown, 'dn.errSaveFailed'));
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    setDeleting(false);
    if (!deliveryId) return;
    setBusy(true);
    setError(null);
    try {
      await deleteDelivery.mutateAsync();
      void navigate('/sales/deliveries');
    } catch (thrown) {
      if (thrown instanceof ApiError && thrown.status === 403) denyPermission('sales.delete');
      setError(localizeServerCause(t, thrown, 'dn.errSaveFailed'));
    } finally {
      setBusy(false);
    }
  };

  /**
   * BR-SAL-03-07's tail / GEN-72: cancelling a confirmed note asks WHY —
   * the ReasonBox IS the confirm (ADM-02 §8: no browser chrome; Escape,
   * click-outside or an empty box abandons the act). The goods return at
   * the cost they left at and the order's delivered figures come back
   * (server-side).
   */
  const onCancelPosted = async (reason: string) => {
    if (!deliveryId) return;
    setCancelAsk(false);
    setBusy(true);
    setError(null);
    try {
      await cancelDelivery.mutateAsync(reason);
      loadedId.current = null;
    } catch (thrown) {
      if (thrown instanceof ApiError && thrown.status === 403) denyPermission('sales.approve');
      setError(localizeServerCause(t, thrown, 'dn.errSaveFailed'));
    } finally {
      setBusy(false);
    }
  };

  if (!isNew && loading) {
    return <div className="h-64 animate-pulse rounded-xl bg-muted" aria-busy />;
  }

  /**
   * SAL-03 §5's two footers, and NEITHER gets a unit — `N-14` decides it:
   * these are Σ over LINES, and one note can carry 2.000 kg of sheet and 4
   * boxes, which is the sum the rule calls meaningless. There is no unit to
   * write and no unit's decimals to round to. The `<Qty>` above prints the
   * unit on every row, which is where `N-4` puts it.
   *
   * The arithmetic is exact BigInt on the 3-decimal strings, not `Number`.
   * It was floats: 0.1 + 0.2 in a quantity column is 0.30000000000000004,
   * and DECISION.md §2.1 is "quantities are strings end to end" precisely so
   * a footer cannot print a figure no line contains.
   */
  const sumQty = (pick: (line: EditLine) => string): string => {
    let total = 0n;
    for (const line of lines) total += toMilliQty(pick(line).trim()) ?? 0n;
    return trimZeros(`${total / 1000n}.${(total % 1000n).toString().padStart(3, '0')}`);
  };
  const totalOrdered = sumQty((line) => line.ordered);
  const totalDelivered = sumQty((line) => line.qty);

  return (
    <div className="grid gap-4">
      <div className="sticky top-0 z-10 flex flex-wrap items-center gap-3 bg-background">
        <h2 className="text-lg font-bold">{t('dn.editorTitle')}</h2>
        <span className="rounded-md bg-muted px-2 py-1 font-mono text-sm text-muted-foreground">
          {delivery ? <bdi dir="ltr">{delivery.code}</bdi> : t('dn.codeNew')}
        </span>
        <DocStatusChip status={status} />
        {/* The toolbar's acts, DECLARED — see `document-toolbar.tsx`. */}
        <DocumentToolbar
          header={{ kind: 'deliveryNote', id: deliveryId, dirty }}
          actions={[
            {
              // Not PART 4.6's Cancel row: a link back to the list. It wore
              // the word "Cancel" beside Delete and the posted note's own
              // Cancel act — three verbs, two meanings. "Close" says what it
              // does; "Cancel" is kept for the posted reversal alone.
              id: 'back',
              row: null,
              label: t('common.close'),
              variant: 'outline',
              to: '/sales/deliveries',
            },
            {
              // Wave 2 — the invoice's Print/PDF pair on the delivery note
              // (quantities, no money). Saved notes only.
              id: 'printPdf',
              row: 'print',
              node:
                !isNew && delivery ? (
                  <DocumentActions
                    request={{ kind: 'delivery_note', locale: printLocale, subjectId: delivery.id }}
                    payload={{ kind: 'delivery_note', data: deliveryNoteDocument(delivery) }}
                  />
                ) : null,
            },
            // Wave 4C — Import lines / Download template (shared line tools).
            ...lineImport.acts,
            {
              // The document's question and the role's, kept apart (16.7).
              id: 'delete',
              row: 'delete',
              label: t('common.delete'),
              icon: <Trash2 aria-hidden />,
              variant: 'destructive',
              visible: !isNew && isDraft,
              gate: { permission: 'sales.delete', reasonKey: 'perm.cannot.deleteDeliveries' },
              disabled: busy,
              onAct: () => setDeleting(true),
            },
            {
              id: 'create',
              row: null,
              label: t('dn.loadFromOrder'),
              visible: isNew,
              gate: { permission: 'sales.create', reasonKey: 'perm.cannot.createDeliveries' },
              busy,
              onAct: () => void onCreate(),
            },
            {
              // `disabled`, not `busy`: this Save never drew a spinner, and
              // GEN-60's pair travels together or not at all — declaring
              // `busy` here would ADD a glyph the screen has never shown.
              id: 'save',
              row: 'save',
              label: t('common.save'),
              variant: 'outline',
              visible: !isNew && isDraft,
              gate: { permission: 'sales.edit', reasonKey: 'perm.cannot.editDeliveries' },
              disabled: busy,
              onAct: () => void onSave(),
            },
            {
              // Confirm is `sales.approve` and nothing else. It used to hang
              // off `editable` too, which quietly required `sales.edit` as
              // well — a role granted approval alone saw no button and no
              // reason. The declaration keeps this gate separate from Save's.
              id: 'post',
              row: 'confirm',
              label: t('dn.confirm'),
              icon: <Truck aria-hidden />,
              visible: !isNew && isDraft,
              // BR-INV-04-08's second half on the button itself: while the
              // stock strip reports a shortage, confirming is the OVERRIDE
              // act and its gate is the override grant — a role without it
              // meets the greyed button and its reason (16.7 row 2) rather
              // than the server's refusal after a save. With nothing short,
              // Confirm is `sales.approve` as before.
              gate: anyShort
                ? {
                    permission: OVERRIDE_NEGATIVE_STOCK,
                    reasonKey: 'perm.cannot.overrideNegativeStock',
                  }
                : { permission: 'sales.approve', reasonKey: 'perm.cannot.postDeliveries' },
              busy,
              onAct: () => void onPost(),
            },
            {
              // BR-SAL-03-07's tail — the ONE action a confirmed note offers.
              // Cancelling posts the opposite movements and entry, so it rides
              // the same permission as Confirm. Gone once cancelled.
              id: 'cancel-posted',
              row: 'cancel',
              label: t('dn.cancelPosted'),
              icon: <Trash2 aria-hidden />,
              variant: 'destructive',
              visible: !isNew && status === 'posted',
              gate: { permission: 'sales.approve', reasonKey: 'perm.cannot.cancelDeliveries' },
              disabled: busy,
              onAct: () => setCancelAsk(true),
            },
          ]}
        >
          {/* GEN-58 asks its sentence in the product’s ONE shared box
              (GEN-204, GEN-209 — "deleting a draft" is first on its list).
              `window.confirm` was neither of BR-PERM-15’s two patterns.
              Inside the same guard as the button, so the dialog cannot
              outlive the condition that allows the delete. */}
          {!isNew && isDraft ? (
            <DeleteConfirmBox
              open={deleting}
              detail={t('dn.confirmDelete')}
              onCancel={() => setDeleting(false)}
              onConfirm={() => void onDelete()}
              busy={busy}
            />
          ) : null}
          {/* GEN-72's typed reason. Unguarded exactly as it was — its own
              `open` is the only thing that draws it, and moving it under the
              posted ternary would be a behaviour change, not a tidy-up. */}
          <ReasonBox
            open={cancelAsk}
            title={t('dn.cancelPosted')}
            prompt={t('dn.cancelPrompt')}
            reasonLabel={t('gen72.cancelReasonLabel')}
            confirmLabel={t('gen72.confirmCancel')}
            busy={busy}
            onConfirm={(reason) => void onCancelPosted(reason)}
            onCancel={() => setCancelAsk(false)}
          />
          {/* BR-INV-04-08's second half: the override's typed reason. Only
              a draft that the strip says is short ever opens it, and only
              Confirm does — its own `open` draws it. */}
          <ReasonBox
            open={negativeAsk}
            title={t('dn.negativeStockTitle')}
            prompt={t('dn.negativeStockPrompt')}
            reasonLabel={t('dn.negativeStockReasonLabel')}
            confirmLabel={t('dn.negativeStockConfirm')}
            busy={busy}
            onConfirm={(reason) => void confirmPost(reason)}
            onCancel={() => setNegativeAsk(false)}
          />
          {/* GEN-58's box before the leftover drafts go, in the same guard as
              the button that opens it (below the posted notice). */}
          {status === 'posted' && pendingDraftIds.length > 0 ? (
            <DeleteConfirmBox
              open={leftoverAsk}
              detail={t('dn.confirmDeleteLeftovers', {
                count: pendingDraftIds.length,
                codes: pendingDraftCodes.join(', '),
              })}
              onCancel={() => setLeftoverAsk(false)}
              onConfirm={() => void onDeleteLeftovers()}
              busy={busy}
            />
          ) : null}
        </DocumentToolbar>
      </div>

      {posted || status === 'posted' ? (
        <div className="flex flex-wrap items-center gap-3 rounded-md border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-200">
          <span className="me-auto">{t('dn.postedNotice')}</span>
          {/* A PARTIAL delivery leaves the order open: the next note is one
              click away, with the order pre-picked (D-09's ?orderId= door). */}
          {order?.status === 'partially_delivered' ? (
            <Gated permission="sales.create" reasonKey="perm.cannot.createDeliveries">
              <Button asChild variant="outline" size="sm">
                <Link to={`/sales/deliveries/new?orderId=${delivery?.salesOrderId ?? ''}`}>
                  <Truck aria-hidden /> {t('dn.followUpDelivery')}
                </Link>
              </Button>
            </Gated>
          ) : null}
          {/* A COMPLETE delivery leaves any other draft against the order
              with nothing it can ever post; offer to clear them. */}
          {order?.status === 'fulfilled' && pendingDraftIds.length > 0 ? (
            <Gated permission="sales.delete" reasonKey="perm.cannot.deleteDeliveries">
              <Button
                variant="outline"
                size="sm"
                disabled={busy}
                onClick={() => setLeftoverAsk(true)}
              >
                <Trash2 aria-hidden /> {t('dn.deleteLeftoverDrafts', { count: pendingDraftIds.length })}
              </Button>
            </Gated>
          ) : null}
        </div>
      ) : null}
      {/* Two drafts raised against one order both defaulted to the full
          remainder; whichever confirms second will be refused by the
          cumulative cap (BR-SAL-03-02). Say so while both are still drafts,
          and link each sibling by its code. */}
      {isDraft && !isNew && pendingDraftCodes.length > 0 ? (
        <p
          role="status"
          className="flex flex-wrap items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200"
        >
          <span>{t('dn.pendingDrafts', { count: pendingDraftCodes.length })}</span>
          {pendingDraftIds.map((id, index) => (
            <Link
              key={id}
              to={`/sales/deliveries/${id}`}
              className="rounded bg-amber-200 px-1.5 py-0.5 font-mono text-xs text-amber-900 underline-offset-2 hover:underline dark:bg-amber-800 dark:text-amber-100"
            >
              <bdi dir="ltr">{pendingDraftCodes[index]}</bdi>
            </Link>
          ))}
        </p>
      ) : null}
      {status === 'cancelled' ? (
        <p className="rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-900 dark:border-red-800 dark:bg-red-950 dark:text-red-200">
          {t('dn.cancelledNotice', { reason: delivery?.cancelReason ?? '' })}
        </p>
      ) : null}
      <OverStockStrip show={anyShort} />
      {/* Wave 4C — the line import's chooser, summary and per-row problems. */}
      {lineImport.panel}
      {error ? (
        <p
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
        >
          {error}
        </p>
      ) : null}

      <Card>
        <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {/* Wave 5B — From sales order, or Direct to customer, when the
              company allows the second. */}
          {isNew && directEnabled ? (
            <div className="grid gap-1.5">
              <Label htmlFor="dn-mode">{t('dn.modeLabel')}</Label>
              <NativeSelect
                id="dn-mode"
                value={mode}
                onChange={(event) => setMode(event.target.value === 'direct' ? 'direct' : 'order')}
              >
                <option value="order">{t('dn.modeFromOrder')}</option>
                <option value="direct">{t('dn.modeDirect')}</option>
              </NativeSelect>
            </div>
          ) : null}
          <div className="grid gap-1.5">
            <Label htmlFor={direct ? 'dn-customer' : 'dn-order'}>
              {direct ? t('sales.fCustomer') : t('dn.fOrder')}
            </Label>
            {direct ? (
              <PartnerTypeahead
                entity="customer"
                id="dn-customer"
                label={t('sales.fCustomer')}
                value={directPartnerId || null}
                onChange={(id) => setDirectPartnerId(id ?? '')}
                options={customerPicker.options}
                selected={customerPicker.optionFor(directPartnerId)}
                query={customerPicker.query}
                onQueryChange={customerPicker.setQuery}
                placeholder={t('sales.pickCustomer')}
                emptyLabel={t('picker.noMatchingCustomer')}
                loading={customerPicker.loading}
              />
            ) : isNew ? (
              <Typeahead
                id="dn-order"
                label={t('dn.fOrder')}
                value={salesOrderId || null}
                onChange={(id) => setSalesOrderId(id ?? '')}
                options={orderPicker.options}
                selected={orderPicker.optionFor(salesOrderId)}
                query={orderPicker.query}
                onQueryChange={orderPicker.setQuery}
                placeholder={t('dn.pickOrder')}
                emptyLabel={t('picker.noMatchingOrder')}
                loading={orderPicker.loading}
              />
            ) : (
              <div className="flex h-12 items-center rounded-[11px] border border-input bg-muted/40 px-3 font-mono text-sm">
                {isDirectNote ? (
                  <span className="rounded bg-sky-100 px-1.5 py-0.5 font-sans text-xs text-sky-900 dark:bg-sky-900 dark:text-sky-100">
                    {t('dn.directBadge')}
                  </span>
                ) : (
                  <Ltr value={delivery?.salesOrderCode ?? ''} />
                )}
                <span className="ms-auto truncate ps-2 font-sans text-muted-foreground">
                  {delivery?.partnerName}
                </span>
              </div>
            )}
            {/* Wave 5B — the customer's balance and credit bar, under the
                customer: the server's own figures, drawn and never decided here. */}
            <PartnerBalanceChip partner={partner} className="justify-self-start" />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="dn-date">{t('dn.fDate')}</Label>
            <Input
              id="dn-date"
              type="date"
              dir="ltr"
              value={deliveryDate}
              onChange={(event) => setDeliveryDate(event.target.value)}
              disabled={!isNew && !editable}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="dn-warehouse">{t('dn.fWarehouse')}</Label>
            <NativeSelect
              id="dn-warehouse"
              value={warehouseId}
              onChange={(event) => setWarehouseId(event.target.value)}
              disabled={!isNew && !editable}
            >
              <option value="">{t('grn.pickWarehouse')}</option>
              {activeWarehouses.map((warehouse) => (
                <option key={warehouse.id} value={warehouse.id}>
                  {warehouse.name}
                </option>
              ))}
            </NativeSelect>
          </div>
          <div className="grid gap-1.5">
            <Label>{t('dn.fCost')}</Label>
            <div className="flex h-12 items-center rounded-[11px] border border-input bg-muted/40 px-3 text-sm">
              {delivery && delivery.status === 'posted' ? (
                <Amount value={delivery.costTotal} currency={baseCurrency} className="font-semibold" />
              ) : (
                <span className="text-muted-foreground">{t('dn.costOnConfirm')}</span>
              )}
            </div>
          </div>
          {/* Wave 5B — INV-F2…F6 on the note, defaulted from the order by the
              server at creation and editable while draft. The note draws its
              own (required) warehouse above and has no currency, so both
              cells are left out of the shared block. */}
          <SalesHeaderFields
            idPrefix="dn"
            state={header}
            onChange={(patch) => setHeader((current) => ({ ...current, ...patch }))}
            disabled={!isNew && !editable}
            doc={delivery}
            warehouses={warehouses}
            partnerId={partnerId}
            omit={['warehouse', 'currency']}
          />
        </CardContent>
      </Card>

      {/* Wave 5B — the logistics strip (SAL-03 §5a): who drove, in what,
          when it left, where to. Draft-side content like the header above;
          a confirmed note keeps what it was confirmed with. */}
      <Card>
        <CardContent className="grid gap-4">
          <h3 className="text-sm font-semibold">{t('dn.secLogistics')}</h3>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="grid gap-1.5">
              <Label htmlFor="dn-driver">{t('dn.fDriver')}</Label>
              <Input
                id="dn-driver"
                maxLength={120}
                value={logistics.driver}
                onChange={(event) => setLogistics((c) => ({ ...c, driver: event.target.value }))}
                disabled={!isNew && !editable}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="dn-vehicle-no">{t('dn.fVehicleNo')}</Label>
              <Input
                id="dn-vehicle-no"
                dir="ltr"
                maxLength={40}
                value={logistics.vehicleNo}
                onChange={(event) => setLogistics((c) => ({ ...c, vehicleNo: event.target.value }))}
                disabled={!isNew && !editable}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="dn-vehicle-type">{t('dn.fVehicleType')}</Label>
              <Input
                id="dn-vehicle-type"
                maxLength={40}
                value={logistics.vehicleType}
                onChange={(event) => setLogistics((c) => ({ ...c, vehicleType: event.target.value }))}
                disabled={!isNew && !editable}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="dn-departed-at">{t('dn.fDepartedAt')}</Label>
              <Input
                id="dn-departed-at"
                type="datetime-local"
                dir="ltr"
                value={logistics.departedAt}
                onChange={(event) => setLogistics((c) => ({ ...c, departedAt: event.target.value }))}
                disabled={!isNew && !editable}
              />
            </div>
            <div className="grid gap-1.5 sm:col-span-2 lg:col-span-4">
              <Label htmlFor="dn-delivery-address">{t('dn.fDeliveryAddress')}</Label>
              <Textarea
                id="dn-delivery-address"
                maxLength={300}
                rows={2}
                value={logistics.deliveryAddress}
                onChange={(event) =>
                  setLogistics((c) => ({ ...c, deliveryAddress: event.target.value }))
                }
                disabled={!isNew && !editable}
              />
              <p className="text-xs text-muted-foreground">{t('dn.deliveryAddressHint')}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Wave 5B — proof of delivery (SAL-03 §5d P1..P8), on a CONFIRMED note:
          who received the goods, when, and what they said. Saved through its
          own route — the one post-confirmation write beside the notes — so
          it never touches the locked grid. The signature itself is an
          attachment (the toolbar's panel); this card is the words. */}
      {!isNew && status === 'posted' ? (
        <Card>
          <CardContent className="grid gap-4">
            <div className="flex flex-wrap items-center gap-3">
              <h3 className="text-sm font-semibold">{t('dn.secProof')}</h3>
              {delivery?.receivedBy ? (
                <span className="rounded bg-emerald-100 px-2 py-0.5 text-xs text-emerald-900 dark:bg-emerald-900 dark:text-emerald-100">
                  {t('dn.proofSigned', { name: delivery.receivedBy })}
                </span>
              ) : null}
            </div>
            <p className="text-xs text-muted-foreground">{t('dn.proofHint')}</p>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div className="grid gap-1.5">
                <Label htmlFor="dn-received-by">{t('dn.fReceivedBy')}</Label>
                <Input
                  id="dn-received-by"
                  maxLength={120}
                  value={proof.receivedBy}
                  onChange={(event) => setProof((c) => ({ ...c, receivedBy: event.target.value }))}
                  disabled={busy}
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="dn-received-at">{t('dn.fReceivedAt')}</Label>
                <Input
                  id="dn-received-at"
                  type="datetime-local"
                  dir="ltr"
                  value={proof.receivedAt}
                  onChange={(event) => setProof((c) => ({ ...c, receivedAt: event.target.value }))}
                  disabled={busy}
                />
              </div>
              <div className="grid gap-1.5 sm:col-span-2">
                <Label htmlFor="dn-receiver-note">{t('dn.fReceiverNote')}</Label>
                <Input
                  id="dn-receiver-note"
                  maxLength={500}
                  value={proof.receiverNote}
                  onChange={(event) => setProof((c) => ({ ...c, receiverNote: event.target.value }))}
                  disabled={busy}
                />
              </div>
            </div>
            <div className="flex justify-end">
              <Gated permission="sales.edit" reasonKey="perm.cannot.editDeliveries">
                <Button variant="outline" size="sm" disabled={busy} onClick={() => void onSaveProof()}>
                  {t('dn.saveProof')}
                </Button>
              </Gated>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {isDirectNote || direct ? (
        <p
          role="status"
          className="rounded-md border border-sky-300 bg-sky-50 px-4 py-3 text-sm text-sky-900 dark:border-sky-800 dark:bg-sky-950 dark:text-sky-200"
        >
          {t('dn.directNotice')}
        </p>
      ) : null}

      {!isNew ? (
        <Card>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-11 text-center">#</TableHead>
                  <TableHead>{t('grn.colItem')}
                  {editable ? <DescriptionLangToggle language={descLang} /> : null}
                </TableHead>
                  {/* Wave 5B — the warehouse each line LEAVES FROM, per row. */}
                  <TableHead className="min-w-[130px]">{t('dn.colWarehouse')}</TableHead>
                  <TableHead className="w-24 text-end">{t('dn.colOrdered')}</TableHead>
                  <TableHead className="w-24 text-end">{t('dn.colRemaining')}</TableHead>
                  <TableHead className="w-28 text-end">{t('dn.colQty')}</TableHead>
                  <TableHead className="min-w-[140px]">{t('grn.colNote')}</TableHead>
                  {editable ? <TableHead className="w-11" /> : null}
                </TableRow>
              </TableHeader>
              <TableBody>
                {lines.map((line, index) => {
                  // BR-SAL-03-05 against what was LEFT, not the full order.
                  // An extra line ordered nothing, so it is never short.
                  const short = !line.isExtra && isShortDelivery(line.qty, line.remaining);
                  return (
                    <React.Fragment key={line.key}>
                    <TableRow
                      className={cn(
                        'odd:bg-muted/30',
                        short && 'bg-amber-50 odd:bg-amber-50 dark:bg-amber-950/40 dark:odd:bg-amber-950/40',
                      )}
                    >
                      <TableCell className="text-center font-mono text-muted-foreground">
                        {index + 1}
                      </TableCell>
                      <TableCell>
                        {line.isExtra && editable ? (
                          // Wave 5B — an extra row picks its item here (the
                          // order rows' items are the order's, never picked).
                          <div className="grid gap-1">
                            <ItemCell
                              label={t('dn.cellExtraItem', { line: index + 1 })}
                              picker={itemPicker}
                              value={line.itemId}
                              onPick={(itemId) => pickExtraItem(line.key, itemId)}
                            />
                            <CellInput
                              aria-label={t('dn.cellExtraReason', { line: index + 1 })}
                              placeholder={t('dn.extraReasonPh')}
                              maxLength={200}
                              value={line.extraReason}
                              onChange={(event) => setLine(line.key, { extraReason: event.target.value })}
                            />
                          </div>
                        ) : (
                          <div className="font-medium">{line.description}</div>
                        )}
                        <div className="flex flex-wrap items-center gap-2 font-mono text-xs text-muted-foreground">
                          {line.itemCode ? <Ltr value={line.itemCode} /> : null}
                          {line.isExtra ? (
                            <span className="rounded bg-sky-100 px-1.5 py-0.5 font-sans text-sky-900 dark:bg-sky-900 dark:text-sky-100">
                              {t('dn.extraChip')}
                            </span>
                          ) : null}
                          {line.isExtra && !editable && line.extraReason ? (
                            <span className="font-sans">{line.extraReason}</span>
                          ) : null}
                          {short ? (
                            <span className="rounded bg-amber-200 px-1.5 py-0.5 font-sans text-amber-900 dark:bg-amber-800 dark:text-amber-100">
                              {t('grn.backorder')}
                            </span>
                          ) : null}
                        </div>
                      </TableCell>
                      {/* Wave 5B — the per-line warehouse: the server's
                          resolution (the order line's own, else the header)
                          as the default, overridable per row while draft. The
                          stock warning reads this very shelf. */}
                      <TableCell>
                        {editable ? (
                          <NativeSelect
                            aria-label={t('dn.cellWarehouse', { line: index + 1 })}
                            className="h-9 text-sm"
                            value={line.warehouseId}
                            onChange={(event) => setLine(line.key, { warehouseId: event.target.value })}
                          >
                            {activeWarehouses.map((warehouse) => (
                              <option key={warehouse.id} value={warehouse.id}>
                                {warehouse.name}
                              </option>
                            ))}
                            {line.warehouseId !== '' &&
                            !activeWarehouses.some((warehouse) => warehouse.id === line.warehouseId) ? (
                              <option value={line.warehouseId}>{line.warehouseId}</option>
                            ) : null}
                          </NativeSelect>
                        ) : (
                          <span className="text-sm">
                            {(warehouses ?? []).find((warehouse) => warehouse.id === line.warehouseId)?.name ??
                              ''}
                          </span>
                        )}
                      </TableCell>
                      {/* `N-4` — what the ORDER asked for, in the unit it
                          asked for it in. Nothing on an extra line. */}
                      <TableCell className="text-end">
                        {line.isExtra ? (
                          <span className="text-muted-foreground">—</span>
                        ) : (
                          <Qty
                            value={line.ordered}
                            precision={line.unitPrecision}
                            unit={line.unitSymbol}
                          />
                        )}
                      </TableCell>
                      {/* What was LEFT for this note — the server's ordered
                          minus what earlier posted notes already took. The
                          figure the Backorder chip is measured against. */}
                      <TableCell className="text-end">
                        {line.isExtra ? (
                          <span className="text-muted-foreground">—</span>
                        ) : (
                          <Qty
                            value={line.remaining}
                            precision={line.unitPrecision}
                            unit={line.unitSymbol}
                          />
                        )}
                      </TableCell>
                      {/*
                        * And what is actually LEAVING. A confirmed note is
                        * locked (`BR-SAL-03-07`, R8) — "goods coming back are
                        * a sales return" — so once it is posted this box can
                        * never be typed into again and is a display, which is
                        * exactly the case `N-4`'s input exception does not
                        * cover. Draft keeps its box.
                        */}
                      <TableCell className={editable ? undefined : 'text-end'}>
                        {editable ? (
                          <>
                            <CellInput
                              mono
                              dir="ltr"
                              inputMode="decimal"
                              aria-label={t('dn.cellQty', { line: index + 1 })}
                              value={line.qty}
                              onChange={(event) => setLine(line.key, { qty: event.target.value })}
                            />
                            <LineStockHint available={lineShort(line)} />
                          </>
                        ) : (
                          <Qty
                            value={line.qty}
                            precision={line.unitPrecision}
                            unit={line.unitSymbol}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <CellInput
                          aria-label={t('grn.cellNote', { line: index + 1 })}
                          placeholder={t('grn.notePh')}
                          value={line.note}
                          onChange={(event) => setLine(line.key, { note: event.target.value })}
                          disabled={!editable}
                        />
                      </TableCell>
                      {editable ? (
                        <TableCell>
                          {/* Only an extra row can leave the grid: an order
                              row is lowered to zero (the backorder), never
                              removed. */}
                          {line.isExtra ? (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              aria-label={t('dn.removeExtraLine', { line: index + 1 })}
                              title={t('dn.removeExtraLine', { line: index + 1 })}
                              onClick={() => removeLine(line.key)}
                            >
                              <Trash2 aria-hidden />
                            </Button>
                          ) : null}
                        </TableCell>
                      ) : null}
                    </TableRow>
                    {/* OQ-05-17's sub-table: one block per thing that leaves
                        the warehouse and has to be identified. */}
                    <TrackingRows
                      blocks={blocksFor(line)}
                      text={line.tracking}
                      onChange={(key, value) => setTracking(line.key, key, value)}
                      disabled={!editable}
                      columns={editable ? 8 : 7}
                      line={index + 1}
                    />
                    </React.Fragment>
                  );
                })}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell colSpan={5} className="text-end">
                    <span className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
                      {t('dn.qtyOrdered')}
                    </span>{' '}
                    <bdi dir="ltr" className="font-mono font-semibold">
                      {totalOrdered}
                    </bdi>
                  </TableCell>
                  <TableCell colSpan={editable ? 3 : 2} className="text-start">
                    <span className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
                      {t('dn.qtyDelivered')}
                    </span>{' '}
                    <bdi dir="ltr" className="font-mono font-semibold">
                      {totalDelivered}
                    </bdi>
                  </TableCell>
                </TableRow>
              </TableFooter>
            </Table>
            {/* Wave 5B — a line that is NOT on the order (a sample, a
                replacement). Gated on sales.create: adding goods the order
                never asked for is raising, not editing. */}
            {editable ? (
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <Gated permission="sales.create" reasonKey="perm.cannot.createDeliveries">
                  <Button type="button" variant="outline" size="sm" onClick={addExtraLine}>
                    <Plus aria-hidden /> {t('dn.addExtraLine')}
                  </Button>
                </Gated>
                <span className="text-xs text-muted-foreground">{t('dn.extraHint')}</span>
              </div>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardContent className="grid gap-1.5">
          <Label htmlFor="dn-notes">{t('sales.notes')}</Label>
          <Textarea
            id="dn-notes"
            maxLength={2000}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            disabled={!isNew && !editable && status !== 'posted'}
          />
        </CardContent>
      </Card>
      {/* Wave 2 — INV-H5's files panel on this document (a signed proof of delivery). Hidden
          behind "save first" on a new draft; permission follows the record. */}
      <AttachmentsPanel kind="delivery_note" entityId={deliveryId} />
    </div>
  );
}
